'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import {
  Home,
  Bot,
  ArrowRightLeft,
  Users,
  Gamepad2,
  ChevronRight,
  Trophy,
} from 'lucide-react'
import { Navbar } from '@/components/custom/main/navbar'
import { SiteFooter } from '@/components/custom/main/footer'

const SECTIONS = [
  { icon: Home,          title: 'Home',                   subtitle: "View today's matches",            to: '/home' },
  { icon: Bot,           title: 'AI Football Expert Bot', subtitle: 'Get insights from our AI analyst', to: '/ai'  },
  { icon: ArrowRightLeft,title: 'Transfer News',           subtitle: 'Latest player movements',          to: null   },
  { icon: Users,         title: 'World Cup Squads',        subtitle: 'WC 2026 team rosters',             to: null   },
  { icon: Trophy,        title: 'User Leagues',            subtitle: 'Create or join a prediction league', to: '/user-leagues' },
  { icon: Gamepad2,      title: 'Play Games',              subtitle: 'Football mini-games',              to: '/games' },
]

// WC 2026 opens June 12, 2026
const WC_DATE = new Date('2026-06-12T00:00:00Z')

function getCountdown(target: Date) {
  const diff = target.getTime() - Date.now()
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0 }
  return {
    days:    Math.floor(diff / 86_400_000),
    hours:   Math.floor((diff % 86_400_000) / 3_600_000),
    minutes: Math.floor((diff % 3_600_000)  / 60_000),
  }
}

function CountdownUnit({ value, label }: { value: number; label: string }) {
  return (
    <div className="flex flex-col items-center min-w-[3rem]">
      <span className="text-2xl font-bold text-white leading-none tabular-nums">{value}</span>
      <span className="mt-1 text-[10px] font-medium text-white/60 uppercase tracking-wider">{label}</span>
    </div>
  )
}

function CardInner({ icon: Icon, title, subtitle }: { icon: typeof Home; title: string; subtitle: string }) {
  return (
    <>
      <div
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/8"
        style={{ background: '#2a323b' }}
      >
        <Icon className="h-5 w-5 text-white/65" strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-white leading-snug">{title}</p>
        <p className="mt-0.5 text-xs text-white/38 truncate">{subtitle}</p>
      </div>
      <ChevronRight
        className="h-4 w-4 shrink-0 text-white/20 group-hover:text-white/40 transition-colors"
        strokeWidth={2}
      />
    </>
  )
}

export function LandingPage() {
  const [countdown, setCountdown] = useState(() => getCountdown(WC_DATE))

  useEffect(() => {
    const id = setInterval(() => {
      setCountdown(getCountdown(WC_DATE))
    }, 60_000)
    return () => clearInterval(id)
  }, [])

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#000000' }}>
      <Navbar />

      <main className="flex-1 w-full max-w-lg mx-auto px-4 pt-20 pb-16">

        {/* Heading */}
        <div className="text-center mb-8 px-2">
          <h1 className="text-3xl sm:text-4xl font-bold text-white leading-tight tracking-tight">
            Your Football Hub
          </h1>
          <p className="mt-3 text-sm text-white/40 leading-relaxed max-w-xs mx-auto">
            Stats, predictions, and analysis — all in one place.
          </p>
        </div>

        {/* WC 2026 Countdown Banner */}
        <div className="relative w-full rounded-2xl overflow-hidden mb-4" style={{ minHeight: '80px' }}>
          {/* Gradient background */}
          <div
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(105deg, #0d1b6e 0%, #1e3fa3 22%, #c2185b 46%, #00796b 68%, #558b2f 100%)',
            }}
          />
          {/* Diagonal stripe overlay (stadium field effect) */}
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 6px, rgba(255,255,255,0.6) 6px, rgba(255,255,255,0.6) 12px)',
            }}
          />
          {/* Soft light bloom in center */}
          <div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-24 rounded-full opacity-20 blur-2xl"
            style={{ background: 'radial-gradient(ellipse, #ffffff 0%, transparent 70%)' }}
          />

          {/* Content */}
          <div className="relative z-10 flex items-center justify-between px-5 py-5 gap-4">
            {/* Left: icon + label */}
            <div className="flex items-center gap-3">
              <div
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/20"
                style={{ background: 'rgba(0,0,0,0.35)' }}
              >
                <Trophy className="h-5 w-5 text-white/90" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-sm font-bold text-white leading-snug">FIFA World Cup</p>
                <p className="text-[11px] text-white/55">USA · Canada · Mexico 2026</p>
              </div>
            </div>

            {/* Right: countdown */}
            <div className="flex items-center gap-3 shrink-0">
              <CountdownUnit value={countdown.days}    label="Days"    />
              <span className="text-white/30 text-lg font-light self-start mt-0.5">|</span>
              <CountdownUnit value={countdown.hours}   label="Hours"   />
              <span className="text-white/30 text-lg font-light self-start mt-0.5">|</span>
              <CountdownUnit value={countdown.minutes} label="Minutes" />
            </div>
          </div>
        </div>

        {/* Section cards */}
        <div className="flex flex-col gap-2.5">
          {SECTIONS.map(({ icon, title, subtitle, to }) =>
            to ? (
              <Link
                key={title}
                href={to}
                className="group flex items-center gap-4 rounded-xl border border-white/8 px-4 py-4 transition-all duration-150 hover:border-blue-500/50 active:border-blue-500/70 no-underline"
                style={{ background: '#0e1114' }}
              >
                <CardInner icon={icon} title={title} subtitle={subtitle} />
              </Link>
            ) : (
              <div
                key={title}
                className="group flex items-center gap-4 rounded-xl border border-white/8 px-4 py-4 cursor-default transition-all duration-150 hover:border-blue-500/50 active:border-blue-500/70"
                style={{ background: '#0e1114' }}
              >
                <CardInner icon={icon} title={title} subtitle={subtitle} />
              </div>
            )
          )}
        </div>
      </main>

      <SiteFooter />
    </div>
  )
}
